import { SVG } from '@svgdotjs/svg.js';
import { construct, crush, get, isNumber } from 'radash';
import { snapshot } from 'valtio';

import { CELL_HEIGHT, CELL_PADDING, CELL_WIDTH, ICON_WIDTH } from '@diagram/diagram-dimensions';
import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { ConnectionHelpers } from '@diagram/helpers/ConnectionHelpers';
import { PortHelpers } from '@diagram/helpers/PortHelpers';
import { PowerFlowHelpers } from '@diagram/helpers/PowerFlowHelpers';
import { ZoneHelpers } from '@diagram/helpers/ZoneHelpers';
import { highlightedConnectionState } from '@diagram/hooks/use-highlighted-connection';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { DiagramService } from '@diagram/services/DiagramService';
import { DiagramSyncService } from '@diagram/services/DiagramSyncService';
import { PortService } from '@diagram/services/PortService';
import { PowerFlowService } from '@diagram/services/PowerFlowService';
import { SuggestionService } from '@diagram/services/SuggestionService';
import { TipService } from '@diagram/services/TipService';
import { copyPasteSpecificationsState } from '@diagram/state/copy-paste-specifications';
import { derivedDiagramState, diagram } from '@diagram/state/diagram';
import { zonesState } from '@diagram/state/zones';

import {
    Cable,
    ComponentType,
    DefaultIECInstallationMethod,
    DefaultNECInstallationMethod,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    DiagramConnectionAnchor,
    DiagramConnectionAnchorWithoutPort,
    DiagramConnectionSchema,
    DiagramPosition,
    IECInstallationMethodOptions,
    NECInstallationMethodOptions,
    Point,
    UserTip,
    VoltageType,
} from 'models';
import { CableHelpers } from 'helpers/CableHelpers';
import { diff } from 'helpers/diff';
import { FormatHelpers } from 'helpers/formatters';
import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { LocalStorageService } from 'services/LocalStorageService';
import { state as currentProjectState } from 'state/current-project';
import { state as currentUserState } from 'state/current-user';

import { roundedPath } from '../helpers/roundedPath';

type Port = DiagramConnection['from' | 'to']['port'];

const ConnectionService = {
    addConnection: ({
        from,
        to,
        ...connectionProps
    }: Required<Pick<DiagramConnection, 'from' | 'to'>> & Partial<DiagramConnection>) => {
        const connection = DiagramConnectionSchema.parse({
            ...connectionProps,
            id: connectionProps.id || ConnectionHelpers.generateId(),
            from,
            to,
            createdAt: new Date().toISOString(),
            createdBy: currentUserState.user?.id ?? '',
        });

        diagram.connections[connection.id] = connection;

        return connection.id;
    },

    getById: (connectionId: DiagramConnection['id']) => {
        const diagram = DiagramService.get();

        return diagram.connections[connectionId] || null;
    },

    getByComponentInstanceAndPort: (componentInstanceId: string, port: number | null) => {
        if (port === null) {
            return null;
        }

        const { connections } = diagram;

        return (
            Object.values(connections).find((connection) => {
                return (
                    (connection.from.componentInstanceId === componentInstanceId && connection.from.port === port) ||
                    (connection.to.componentInstanceId === componentInstanceId && connection.to.port === port)
                );
            }) || null
        );
    },

    copySpecifications: (id: DiagramConnection['id']) => {
        copyPasteSpecificationsState.connection = ConnectionService.getById(id);
    },

    pasteSpecifications: (id: DiagramConnection['id']) => {
        const connection = ConnectionService.getById(id);
        const { connection: copy } = snapshot(copyPasteSpecificationsState);

        if (connection && copy) {
            DiagramSyncService.updateByKey(
                `connections.${connection.id}.requirements.voltage`,
                copy.requirements.voltage,
                true,
            );
            DiagramSyncService.updateByKey(
                `connections.${connection.id}.requirements.current`,
                copy.requirements.current,
                true,
            );
            DiagramSyncService.updateByKey(`connections.${connection.id}.multiple`, copy.multiple, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.lines`, copy.lines, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.wireSize`, copy.wireSize, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.length`, copy.length, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.resistance`, copy.resistance, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.inductance`, copy.inductance, true);
            DiagramSyncService.updateByKey(`connections.${connection.id}.pe`, copy.pe, true);
            DiagramSyncService.updateByKey(
                `connections.${connection.id}.conductorMaterial`,
                copy.conductorMaterial,
                true,
            );
            DiagramSyncService.updateByKey(`connections.${connection.id}.installation`, copy.installation, true);
        }
    },

    getConnectionsForComponentInstance: (
        diagram: Diagram = DiagramService.get(),
        componentInstance: DiagramComponentInstance,
    ) => {
        const { connections } = diagram;

        return Object.values(connections).filter((connection) => {
            return (
                connection.from.componentInstanceId === componentInstance.id ||
                connection.to.componentInstanceId === componentInstance.id
            );
        });
    },

    getInternalConnectionsForComponentInstances: (
        componentInstanceIds: DiagramComponentInstance['id'][],
        diagram = DiagramService.get(),
    ) => {
        const { connections } = diagram;

        return Object.values(connections).filter((connection) => {
            return (
                componentInstanceIds.includes(connection.from.componentInstanceId) &&
                componentInstanceIds.includes(connection.to.componentInstanceId)
            );
        });
    },

    getIconPoint: (anchor: DiagramConnectionAnchor, diagram = DiagramService.get()): Point | undefined => {
        const { componentInstances } = diagram;
        const { componentInstanceId: id, edge, offset } = anchor;

        const componentInstance = componentInstances[id];

        if (!componentInstance) {
            return undefined;
        }

        const { position, rowSpan = 1, colSpan = 1 } = componentInstance;

        const { x, y } = position;

        const point = {
            x: 0,
            y: 0,
        };

        if (edge === 'top') {
            point.x = (x + offset) * CELL_WIDTH + CELL_WIDTH - CELL_PADDING - ICON_WIDTH / 2;
            point.y = y * CELL_HEIGHT + CELL_PADDING + 1;
        } else if (edge === 'bottom') {
            point.x = (x + offset) * CELL_WIDTH + CELL_WIDTH - CELL_PADDING - ICON_WIDTH / 2;
            point.y = (y + rowSpan) * CELL_HEIGHT - CELL_PADDING - 1;
        } else if (edge === 'left') {
            point.x = (x + 1) * CELL_WIDTH - CELL_PADDING - ICON_WIDTH + 1;
            point.y = (y + offset) * CELL_HEIGHT + CELL_PADDING + ICON_WIDTH / 2;
        } else if (edge === 'right') {
            point.x = (x + colSpan) * CELL_WIDTH - CELL_PADDING - 1;
            point.y = (y + offset) * CELL_HEIGHT + CELL_PADDING + ICON_WIDTH / 2;
        }

        return point;
    },

    getCellPoint: (anchor: DiagramConnectionAnchor, diagram = DiagramService.get()): Point | undefined => {
        const { componentInstances } = diagram;
        const { componentInstanceId: id, edge, offset } = anchor;

        const componentInstance = componentInstances[id];

        if (!componentInstance) {
            return undefined;
        }

        const { position, rowSpan = 1, colSpan = 1 } = componentInstance;

        const { x, y } = position;

        const point = {
            x: 0,
            y: 0,
        };

        if (edge === 'top') {
            point.x = (x + offset) * CELL_WIDTH + CELL_WIDTH - CELL_PADDING - ICON_WIDTH / 2;
            point.y = y * CELL_HEIGHT;
        } else if (edge === 'bottom') {
            point.x = (x + offset) * CELL_WIDTH + CELL_WIDTH - CELL_PADDING - ICON_WIDTH / 2;
            point.y = (y + rowSpan) * CELL_HEIGHT;
        } else if (edge === 'left') {
            point.x = x * CELL_WIDTH;
            point.y = (y + offset) * CELL_HEIGHT + CELL_PADDING + ICON_WIDTH / 2;
        } else if (edge === 'right') {
            point.x = (x + colSpan) * CELL_WIDTH;
            point.y = (y + offset) * CELL_HEIGHT + CELL_PADDING + ICON_WIDTH / 2;
        }

        return point;
    },

    getPathPoints: (
        from: DiagramConnectionAnchor,
        to: DiagramConnectionAnchor,
        diagram = DiagramService.get(),
    ): Point[] | undefined => {
        const p2 = ConnectionService.getCellPoint(from, diagram);
        const p4 = ConnectionService.getCellPoint(to, diagram);

        if (!p2 || !p4) {
            return undefined;
        }

        const dx = p4.x - p2.x;
        const dy = p4.y - p2.y;

        const lr = from.edge === 'left' || from.edge === 'right';
        const tb = from.edge === 'top' || from.edge === 'bottom';

        const p3 = {
            x: p2.x + (lr ? dx : 0),
            y: p2.y + (tb ? dy : 0),
        };

        return [
            ConnectionService.getIconPoint(from, diagram),
            p2,
            p3,
            p4,
            ConnectionService.getIconPoint(to, diagram),
        ].filter((point, index, points) => {
            if (!point) {
                return false;
            }

            const { x, y } = point;

            const { x: x_, y: y_ } = points[index - 1] || {};

            return x !== x_ || y !== y_;
        }) as Point[];
    },

    getPath: (
        from: DiagramConnectionAnchor,
        to: DiagramConnectionAnchor,
        diagram = DiagramService.get(),
    ):
        | {
              separatePaths: string[];
              path: string;
              points: Point[];
              length: number;
              getPointAtPosition: (position: number) => Point;
          }
        | undefined => {
        const points = ConnectionService.getPathPoints(from, to, diagram);

        if (!points) {
            return;
        }

        const radius = 8;

        const pathWithRoundedCorners = roundedPath({ points, radius });

        const separatePaths: string[] = points.reduce((res: string[], point, index) => {
            const nextPoint = points[index + 1];

            if (index < points.length - 1) {
                res.push(`M ${point.x} ${point.y} L ${nextPoint.x} ${nextPoint.y}`);
            }

            return res;
        }, []);

        const draw = SVG();
        const path = draw.path(pathWithRoundedCorners);
        const length = path.length();
        const getPointAtPosition = (position: number) => {
            return path.pointAt(position);
        };

        return {
            path: pathWithRoundedCorners,
            points,
            separatePaths,
            length,
            getPointAtPosition,
        };
    },

    // TODO: fix this type, technically it can be a crushed object as well
    update: (values: Partial<DiagramConnection> & { id: DiagramConnection['id'] }) => {
        const { id, ...rest } = values;

        const data: { [k: string]: any } = {};

        const connection = ConnectionService.getById(id);
        if (!connection) return;

        const branch = ConnectionService.branch(connection);
        const zone = ConnectionService.zone(connection);

        const syncable = {
            branch: [
                'requirements.current.value',
                'lines',
                'lines.AC',
                'lines.AC.L1',
                'lines.AC.L2',
                'lines.AC.L3',
                'lines.AC.N',
                'lines.AC.PE',
                'lines.DC',
                'lines.DC.L+',
                'lines.DC.M',
                'lines.DC.L-',
                'lines.DC.PE',
            ],
            zone: ['requirements.voltage.value', 'lineToEarthVoltage', 'lineToLineVoltage'],
        };

        const { updates } = diff(connection, construct(values));

        Object.entries(rest).forEach(([key, value]) => {
            if (syncable.branch.includes(key)) {
                branch.forEach((connection) => {
                    data[`connections.${connection.id}.${key}`] = value;
                });
            } else if (zone && syncable.zone.includes(key)) {
                zone.connections.forEach((connection) => {
                    data[`connections.${connection.id}.${key}`] = value;
                });
            } else {
                data[`connections.${id}.${key}`] = value;
            }
        });

        DiagramSyncService.update(data);

        const updated = ConnectionService.getById(id);

        ConnectionService.effects.syncCircuitBreakerPoles(updated, updates);
    },

    effects: {
        syncCircuitBreakerPoles: (updated: DiagramConnection, updates: Record<string, any>) => {
            let ACLinesUpdated = false;

            Object.keys(updates).forEach((key) => {
                ACLinesUpdated = ACLinesUpdated || !!key.match(/lines\.AC\.(L1|L2|L3|N)/);
            });

            if (ACLinesUpdated) {
                const lines = updated.lines.AC;

                const update = (componentInstance: DiagramComponentInstance) => {
                    if (componentInstance?.componentType !== 'breaker') {
                        return;
                    }

                    let poles = '';

                    if (lines.L1 && !lines.L2 && !lines.L3 && lines.N) {
                        poles = '2P';
                    }

                    if (lines.L1 && lines.L2 && lines.L3 && !lines.N) {
                        poles = '3P';
                    }

                    if (lines.L1 && lines.L2 && lines.L3 && lines.N) {
                        poles = '4P';
                    }

                    if (poles) {
                        ComponentInstanceService.update(
                            componentInstance.id,
                            {
                                'specifications.electrical.ports.0.AC.poles': poles,
                                'specifications.electrical.ports.1.AC.poles': poles,
                            },
                            {
                                effects: false,
                            },
                        );
                    }
                };

                update(ComponentInstanceService.getById(updated.from.componentInstanceId));
                update(ComponentInstanceService.getById(updated.to.componentInstanceId));
            }
        },
    },

    updateVoltageType: (connection: DiagramConnection, voltageType: 'AC' | 'DC') => {
        const { from } = connection;

        const fromComponentInstance = ComponentInstanceService.getById(from.componentInstanceId);

        PortService.updateVoltageType(fromComponentInstance.id, from.port, voltageType, true);
    },

    updatePort: (connection: DiagramConnection, endpoint: 'from' | 'to', port: Port) => {
        const { componentInstanceId } = connection[endpoint];
        const historical = diagram.connections[connection.id][endpoint].port;

        Object.values(diagram.connections).forEach((needle) => {
            if (needle.from.componentInstanceId === componentInstanceId && needle.from.port === port) {
                needle.from.port = historical;
            }

            if (needle.to.componentInstanceId === componentInstanceId && needle.to.port === port) {
                needle.to.port = historical;
            }
        });

        diagram.connections[connection.id][endpoint].port = port;
    },

    removePort: (connectionId: string, endpoint: 'from' | 'to') => {
        DiagramSyncService.updateByKey(`connections.${connectionId}.${endpoint}.port`, null, true);
    },

    setAnchorOffset: (connectionId: DiagramConnection['id'], endpoint: 'from' | 'to', offset: number) => {
        DiagramSyncService.updateByKey(`connections.${connectionId}.${endpoint}.offset`, offset, true);
    },

    moveConnectionAnchor: (
        connection: DiagramConnection,
        current: DiagramConnectionAnchor,
        next: DiagramConnectionAnchor,
    ) => {
        const fixedAnchor =
            current.componentInstanceId === connection.from.componentInstanceId ? connection.to : connection.from;

        if (fixedAnchor.componentInstanceId === next.componentInstanceId) {
            throw new Error('Cannot connect a component to itself');
        }

        // Landing on an occupied anchor would stack two connections on the same
        // anchor; only one of them stays reachable through the anchor dot, while
        // the other keeps blocking resizes as an invisible connected port (DEV-9503)
        const connectionOnNextAnchor = ConnectionService.getConnectionForAnchor(next);

        if (connectionOnNextAnchor && connectionOnNextAnchor.id !== connection.id) {
            LocalNotificationService.showInfo({
                message: 'Cannot move a connection to an anchor that is already connected',
            });

            return;
        }

        const diagram = DiagramSyncService.get();

        const nextComponentInstance = ComponentInstanceService.getById(next.componentInstanceId, diagram);
        const currentComponentInstance = ComponentInstanceService.getById(current.componentInstanceId, diagram);

        const voltageType =
            current.port !== null
                ? PortHelpers.getVoltageTypeConfiguration(currentComponentInstance, current.port)
                : null;

        const isMovingToSameComponent = current.componentInstanceId === next.componentInstanceId;

        // If portToConnectTo is null, it means that no port is available on the component
        // Next, try and create a new port on the next component
        // If still null is returned, it means that the component has reached the maximum number of ports
        const portToConnectTo =
            next.port ??
            (isMovingToSameComponent
                ? current.port
                : (ComponentInstanceService.getNextAvailablePort(next.componentInstanceId) ??
                  ComponentInstanceService.addDynamicPort(nextComponentInstance)));

        if (portToConnectTo !== null) {
            if (connection.from.componentInstanceId === current.componentInstanceId) {
                DiagramSyncService.updateByKey(
                    `connections.${connection.id}.from`,
                    {
                        ...connection.from,
                        ...next,
                        port: portToConnectTo,
                    },
                    true,
                );
            } else if (connection.to.componentInstanceId === current.componentInstanceId) {
                DiagramSyncService.updateByKey(
                    `connections.${connection.id}.to`,
                    {
                        ...connection.to,
                        ...next,
                        port: portToConnectTo,
                    },
                    true,
                );
            }

            if (voltageType) {
                PortService.updateVoltageType(nextComponentInstance.id, portToConnectTo, voltageType, true);
            }
        }

        if (portToConnectTo === null) {
            LocalNotificationService.showToast({
                description: 'Cannot move connection anchor because the maximum number of connections has been reached',
            });
        }
    },

    delete: (idOrArray: DiagramConnection['id'] | DiagramConnection['id'][]): void => {
        if (typeof idOrArray === 'string') {
            return ConnectionService.delete([idOrArray]);
        }

        idOrArray.forEach((connectionId) => {
            delete diagram.connections[connectionId];
        });
    },

    isValidConnection: (from: DiagramConnectionAnchor, to: DiagramConnectionAnchor): boolean => {
        const diagram = DiagramService.get();

        const { componentInstances } = diagram;

        // Do not allow connections to the same component
        if (from.componentInstanceId === to.componentInstanceId) {
            return false;
        }

        const fromComponent = componentInstances[from.componentInstanceId];
        const toComponent = componentInstances[to.componentInstanceId];

        // Do not allow connections to components that do not exist
        if (!fromComponent || !toComponent) {
            return false;
        }

        const x_ =
            fromComponent.position.x +
            (from.edge === 'top' || from.edge === 'bottom' ? from.offset : 0) +
            (from.edge === 'right' ? fromComponent.colSpan - 1 : 0);
        const y_ =
            fromComponent.position.y +
            (from.edge === 'left' || from.edge === 'right' ? from.offset : 0) +
            (from.edge === 'bottom' ? fromComponent.rowSpan - 1 : 0);

        const x =
            toComponent.position.x +
            (to.edge === 'top' || to.edge === 'bottom' ? to.offset : 0) +
            (to.edge === 'right' ? toComponent.colSpan - 1 : 0);
        const y =
            toComponent.position.y +
            (to.edge === 'left' || to.edge === 'right' ? to.offset : 0) +
            (to.edge === 'bottom' ? toComponent.rowSpan - 1 : 0);

        if (x == x_ && y < y_) {
            if (from.edge === 'top' && to.edge === 'bottom') return true;
            else if (from.edge === 'left' && to.edge === 'left') return true;
            else if (from.edge === 'right' && to.edge === 'right') return true;
        } else if (x == x_ && y > y_) {
            if (from.edge === 'bottom' && to.edge === 'top') return true;
            else if (from.edge === 'left' && to.edge === 'left') return true;
            else if (from.edge === 'right' && to.edge === 'right') return true;
        } else if (y == y_ && x < x_ && from.edge === 'left' && to.edge === 'right') return true;
        else if (y == y_ && x > x_ && from.edge === 'right' && to.edge === 'left') return true;
        else if (x < x_ && y < y_) {
            if (from.edge === 'left' && to.edge === 'bottom') return true;
            else if (from.edge === 'top' && to.edge === 'right') return true;
        } else if (x < x_ && y > y_) {
            if (from.edge === 'left' && to.edge === 'top') return true;
            if (from.edge === 'bottom' && to.edge === 'right') return true;
        } else if (x > x_ && y < y_) {
            if (from.edge === 'right' && to.edge === 'bottom') return true;
            if (from.edge === 'top' && to.edge === 'left') return true;
        } else if (x > x_ && y > y_) {
            if (from.edge === 'right' && to.edge === 'top') return true;
            if (from.edge === 'bottom' && to.edge === 'left') return true;
        }

        return false;
    },

    updateAnchors: (connection: DiagramConnection) => {
        // update connection anchors so that the connection is valid

        const isValid = ConnectionService.isValidConnection(connection.from, connection.to);

        if (isValid) return;

        const fromComponentInstance = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(connection.to.componentInstanceId);

        if (!fromComponentInstance || !toComponentInstance) return;

        const fromAnchors = ComponentInstanceService.getAnchors(fromComponentInstance);
        const toAnchors = ComponentInstanceService.getAnchors(toComponentInstance);

        const options: {
            distance: number;
            from: any;
            to: any;
        }[] = [];

        const getPoint = (componentInstance: DiagramComponentInstance, anchor: DiagramConnectionAnchor) => {
            return {
                left: {
                    x: componentInstance.position.x,
                    y: componentInstance.position.y + anchor.offset,
                },
                right: {
                    x: componentInstance.position.x + componentInstance.colSpan,
                    y: componentInstance.position.y + anchor.offset,
                },
                top: {
                    x: componentInstance.position.x + anchor.offset,
                    y: componentInstance.position.y,
                },
                bottom: {
                    x: componentInstance.position.x + anchor.offset,
                    y: componentInstance.position.y + componentInstance.rowSpan,
                },
            }[anchor.edge];
        };

        // find matching anchors for which the connection is valid
        for (const fromAnchor of fromAnchors) {
            for (const toAnchor of toAnchors) {
                const isOriginalFromAnchor = ConnectionService.isEqual(fromAnchor, connection.from);
                const isOriginalToAnchor = ConnectionService.isEqual(toAnchor, connection.to);

                if (isOriginalFromAnchor && isOriginalToAnchor) continue;

                const fromAnchorIsValid = isOriginalFromAnchor || !ConnectionService.getConnectionForAnchor(fromAnchor);
                const toAnchorIsValid = isOriginalToAnchor || !ConnectionService.getConnectionForAnchor(toAnchor);
                const newConnectionIsValid = ConnectionService.isValidConnection(fromAnchor, toAnchor);

                if (fromAnchorIsValid && toAnchorIsValid && newConnectionIsValid) {
                    const from = getPoint(fromComponentInstance, fromAnchor);
                    const to = getPoint(toComponentInstance, toAnchor);

                    const deltaX = to.x - from.x;
                    const deltaY = to.y - from.y;
                    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                    options.push({
                        distance,
                        from: fromAnchor,
                        to: toAnchor,
                    });
                }
            }
        }

        if (options.length) {
            let shortest = options[0];

            options.forEach((option) => {
                if (option.distance < shortest.distance) {
                    shortest = option;
                }
            });

            ConnectionService.moveConnectionAnchor(connection, connection.from, {
                ...shortest.from,
                port: connection.from.port,
            });
            ConnectionService.moveConnectionAnchor(connection, connection.to, {
                ...shortest.to,
                port: connection.to.port,
            });
        }
    },

    getConnectionForAnchor: (anchor: DiagramConnectionAnchorWithoutPort): DiagramConnection | undefined => {
        const { connections } = DiagramService.get();

        return Object.values(connections).find(
            (connection) =>
                ConnectionService.isEqual(connection.from, anchor) || ConnectionService.isEqual(connection.to, anchor),
        );
    },

    isEqual: (a: DiagramConnectionAnchorWithoutPort | null, b: DiagramConnectionAnchorWithoutPort | null): boolean => {
        if (a === null && b === null) return true;
        else if (a === null || b === null) return false;

        return a.componentInstanceId === b.componentInstanceId && a.offset === b.offset && a.edge === b.edge;
    },

    getConnectionsCrossingPosition: (position: DiagramPosition): DiagramConnection[] => {
        const { connections } = DiagramService.get();

        return Object.values(connections).filter((connection) => ConnectionService.crosses(connection, position));
    },

    /**
     * Return true when a `connection` crosses a `position`
     *
     * @param connection
     * @param position
     * @returns true when a connection crosses a position
     */
    crosses: (connection: DiagramConnection, position: DiagramPosition): boolean => {
        const points = ConnectionService.getPathPoints(connection.from, connection.to) || [];

        const X = position.x;
        const Y = position.y;

        for (let i = 1; i < points.length; i++) {
            const p1 = points[i - 1];
            const p2 = points[i];

            const x1 = Math.floor(p1.x / CELL_WIDTH);
            const y1 = Math.floor(p1.y / CELL_HEIGHT);
            const x2 = Math.floor(p2.x / CELL_WIDTH);
            const y2 = Math.floor(p2.y / CELL_HEIGHT);

            if (x1 === x2 && x1 === X && Y >= Math.min(y1, y2) && Y <= Math.max(y1, y2)) return true;
            if (y1 === y2 && y1 === Y && X >= Math.min(x1, x2) && X <= Math.max(x1, x2)) return true;
        }

        return false;
    },

    addConnectionWithEffects: ({
        from,
        to,
        voltageTypeOverride,
        connectionProps = {},
    }: {
        from: DiagramConnectionAnchor;
        to: DiagramConnectionAnchor;
        voltageTypeOverride?: VoltageType | null;
        connectionProps?: Partial<Omit<DiagramConnection, 'id' | 'to' | 'from'>>;
    }): string => {
        if (ConnectionService.getConnectionForAnchor(from) || ConnectionService.getConnectionForAnchor(to)) {
            LocalNotificationService.showInfo({
                message: 'Cannot create a connection to an anchor that is already connected',
            });

            return '';
        }

        const fromComponentInstance = ComponentInstanceService.getById(from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(to.componentInstanceId);

        if (
            DiagramService.getMaximumNumberOfConnections(fromComponentInstance) === 0 ||
            DiagramService.getMaximumNumberOfConnections(toComponentInstance) === 0
        ) {
            LocalNotificationService.showInfo({
                message: 'Cannot create a connection to a controller',
            });

            return '';
        }

        const showResizeTipConditions = [
            fromComponentInstance.componentType === 'bus' && toComponentInstance.componentType === 'bus',
            fromComponentInstance.componentType === 'panel' && toComponentInstance.componentType === 'panel',
            fromComponentInstance.componentType === 'combinerBox' &&
                toComponentInstance.componentType === 'combinerBox',
        ];

        if (showResizeTipConditions.some(Boolean)) {
            TipService.trigger(UserTip.RESIZE_COMPONENT);
        }

        const diagram = DiagramService.get();

        const prepareComponentInstanceForConnecting = (componentInstance: DiagramComponentInstance) => {
            const numberOfPorts = componentInstance.specifications.electrical.ports.length;
            const numberOfConnections = DiagramService.getNumberOfConnections(componentInstance);

            if (numberOfConnections >= numberOfPorts && !componentInstance.componentId) {
                ComponentInstanceService.addDynamicPort(componentInstance);
            }

            if (DiagramService.hasMaximumNumberOfConnections(componentInstance)) {
                const connections = ConnectionService.getConnectionsForComponentInstance(diagram, componentInstance);

                ConnectionService.delete(connections[connections.length - 1].id);
            }
        };

        prepareComponentInstanceForConnecting(fromComponentInstance);
        prepareComponentInstanceForConnecting(toComponentInstance);

        const getMostConnectedVoltageType = (componentInstance: DiagramComponentInstance) => {
            const voltageTypes = {
                AC: 0,
                DC: 0,
            };

            ConnectionService.getConnectionsForComponentInstance(diagram, componentInstance).forEach((connection) => {
                const voltageType = ConnectionService.getVoltageType(connection);

                if (voltageType) {
                    voltageTypes[voltageType]++;
                }
            });

            if (voltageTypes.AC > voltageTypes.DC) {
                return {
                    voltageType: 'AC' as VoltageType,
                    count: voltageTypes.AC,
                };
            } else if (voltageTypes.DC > voltageTypes.AC) {
                return {
                    voltageType: 'DC' as VoltageType,
                    count: voltageTypes.DC,
                };
            } else {
                return null;
            }
        };

        let preferredVoltageType: VoltageType | null = null;

        const mostConnectedFromVoltageType = getMostConnectedVoltageType(fromComponentInstance);
        const mostConnectedToVoltageType = getMostConnectedVoltageType(toComponentInstance);

        if (mostConnectedFromVoltageType && mostConnectedToVoltageType) {
            if (mostConnectedFromVoltageType.count > mostConnectedToVoltageType.count) {
                preferredVoltageType = mostConnectedFromVoltageType.voltageType;
            } else {
                preferredVoltageType = mostConnectedToVoltageType.voltageType;
            }
        } else if (mostConnectedFromVoltageType) {
            preferredVoltageType = mostConnectedFromVoltageType.voltageType;
        } else if (mostConnectedToVoltageType) {
            preferredVoltageType = mostConnectedToVoltageType.voltageType;
        }

        const matchPortsResult = PortService.resolveFreeEndpointPorts({
            from,
            to,
            fromComponentInstance,
            toComponentInstance,
            preferredVoltageType,
            voltageTypeOverride,
        });

        const { fromPort, toPort } = matchPortsResult;
        const suggestedVoltageType = voltageTypeOverride ?? matchPortsResult.suggestedVoltageType;

        const connectionId = ConnectionService.addConnection({
            from,
            to,
            ...connectionProps,
        });

        DiagramSyncService.updateByKey(`connections.${connectionId}.from.port`, fromPort?.port.key ?? null, true);
        DiagramSyncService.updateByKey(`connections.${connectionId}.to.port`, toPort?.port.key ?? null, true);

        const onlyOneEndpointIsFixed = (from.port === null) !== (to.port === null);

        if (onlyOneEndpointIsFixed && fromPort && isNumber(fromPort.port.key) && toPort && isNumber(toPort.port.key)) {
            const fromIsFixed = from.port !== null;
            const fixedComponentInstance = fromIsFixed ? fromComponentInstance : toComponentInstance;
            const fixedPortKey = (fromIsFixed ? fromPort : toPort).port.key;
            const freeComponentInstance = fromIsFixed ? toComponentInstance : fromComponentInstance;
            const freePortKey = (fromIsFixed ? toPort : fromPort).port.key;

            const fixedDirection = fixedComponentInstance.configuration.ports[fixedPortKey]?.powerFlowDirection;
            const freePort = { componentInstanceId: freeComponentInstance.id, port: freePortKey };

            const followFixedEndPlan =
                fixedDirection &&
                PowerFlowService.getBestPowerFlowPlan(freePort, [
                    PowerFlowHelpers.getReversedPowerFlowDirection(fixedDirection),
                ]);

            if (followFixedEndPlan) {
                PowerFlowService.applyPowerFlowPlan(followFixedEndPlan);
            }

            const hasPowerFlowDirection = !!PowerFlowHelpers.getRelativePowerFlowDirection(
                ConnectionService.getById(connectionId),
                fromComponentInstance,
                toComponentInstance,
            );

            const fallbackPlan =
                !hasPowerFlowDirection &&
                PowerFlowService.getBestPowerFlowPlan(freePort, ['bidirectional', 'input', 'output']);

            if (fallbackPlan) {
                PowerFlowService.applyPowerFlowPlan(fallbackPlan);
            }
        } else if (fromPort && isNumber(fromPort?.port.key) && toPort && isNumber(toPort?.port.key)) {
            const plan = PowerFlowService.getBestPowerFlowPlan(
                { componentInstanceId: fromComponentInstance.id, port: fromPort.port.key },
                ['bidirectional', 'input', 'output'],
            );

            if (plan) {
                PowerFlowService.applyPowerFlowPlan(plan);
            }
        }

        if (suggestedVoltageType && fromPort && toPort) {
            PortService.updateVoltageType(fromComponentInstance.id, fromPort.port.key, suggestedVoltageType, false);
            PortService.updateVoltageType(toComponentInstance.id, toPort.port.key, suggestedVoltageType, false);

            const fromPortSpecifications =
                // @ts-ignore
                fromComponentInstance.specifications.electrical.ports[fromPort.port.key][suggestedVoltageType];
            const toPortSpecifications =
                // @ts-ignore
                toComponentInstance.specifications.electrical.ports[toPort.port.key][suggestedVoltageType];

            if (!fromPortSpecifications || !toPortSpecifications) {
                console.error({
                    fromComponentInstance,
                    fromPort,
                    fromPortSpecifications,
                    toComponentInstance,
                    toPort,
                    toPortSpecifications,
                    suggestedVoltageType,
                });
            }

            const fromVoltage = fromPortSpecifications.voltage;
            const toVoltage = toPortSpecifications.voltage;

            if (fromVoltage.max && toVoltage.max) {
                const max = Math.max(fromVoltage.max, toVoltage.max);

                DiagramSyncService.updateByKey(`connections.${connectionId}.requirements.voltage.value`, max, true);
            }

            const fromCurrent = fromPortSpecifications.current;
            const toCurrent = toPortSpecifications.current;

            if (fromCurrent.max && toCurrent.max) {
                const max = Math.min(fromCurrent.max, toCurrent.max);

                DiagramSyncService.updateByKey(`connections.${connectionId}.requirements.current.value`, max, true);
            }
        }

        if (fromPort && isNumber(fromPort.port.key) && toPort && isNumber(toPort.port.key) && suggestedVoltageType) {
            const specifications = {
                // @ts-ignore suggestedVoltageType exists
                from: fromComponentInstance.specifications.electrical.ports[fromPort.port.key][suggestedVoltageType],
                // @ts-ignore suggestedVoltageType exists
                to: toComponentInstance.specifications.electrical.ports[toPort.port.key][suggestedVoltageType],
            };

            const syncSpecifications = (
                measurement: 'current' | 'power' | 'voltage',
                setPortValue:
                    | typeof PortService.updateCurrent
                    | typeof PortService.updatePower
                    | typeof PortService.updateVoltage,
            ) => {
                if (!(measurement in specifications.from) || !(measurement in specifications.to)) {
                    return;
                }

                const hasFromMeasurement = FormatHelpers.isEmpty(specifications.from[measurement]);
                const hasToMeasurement = FormatHelpers.isEmpty(specifications.to[measurement]);

                if (hasFromMeasurement && !hasToMeasurement && !toComponentInstance.componentId) {
                    const value =
                        // @ts-ignore suggestedVoltageType exists
                        fromComponentInstance.specifications.electrical.ports[fromPort.port.key!][suggestedVoltageType][
                            measurement
                        ];

                    setPortValue(toComponentInstance, toPort.port.key, suggestedVoltageType, value);
                }

                if (hasToMeasurement && !hasFromMeasurement && !fromComponentInstance.componentId) {
                    const value =
                        // @ts-ignore suggestedVoltageType exists
                        toComponentInstance.specifications.electrical.ports[toPort.port.key][suggestedVoltageType][
                            measurement
                        ];

                    setPortValue(fromComponentInstance, fromPort.port.key, suggestedVoltageType, value);
                }
            };

            syncSpecifications('power', PortService.updatePower);
        }

        const installationDefaults = ConnectionService.getDefaultInstallationProperties();

        Object.entries(installationDefaults).forEach(([key, value]) => {
            DiagramSyncService.updateByKey(`connections.${connectionId}.installation.${key}`, value, true);
        });

        if (currentProjectState.project) {
            DiagramSyncService.updateByKey(
                `connections.${connectionId}.requirements.voltageDrop`,
                currentProjectState.project.wireSizing.voltageDrop,
                true,
            );
        }

        const connection = ConnectionService.getById(connectionId);

        if (connection && !connection.requirements.voltage.value) {
            const zones = ZoneHelpers.generateControlZones(diagram, []);
            const zone = ZoneHelpers.getZoneForConnection(zones, connectionId);

            if (zone) {
                DiagramSyncService.updateByKey(
                    `connections.${connectionId}.requirements.voltage.value`,
                    ZoneHelpers.getDesignVoltage(zone),
                    true,
                );
            }
        }

        const connectsGrounding =
            fromComponentInstance.componentType === 'grounding' || toComponentInstance.componentType === 'grounding';

        if (connectsGrounding) {
            DiagramSyncService.updateByKey(
                `connections.${connectionId}.lines.DC`,
                { 'L+': false, 'M': false, 'L-': false, 'PE': true },
                true,
            );
        }

        return connectionId;
    },

    getVoltageType: (connection?: DiagramConnection | null) => {
        if (!connection || !connection.from || !connection.to) {
            return null;
        }

        const { from, to } = connection;
        const fromComponentInstance = ComponentInstanceService.getById(from.componentInstanceId)!;
        const toComponentInstance = ComponentInstanceService.getById(to.componentInstanceId)!;

        return ConnectionHelpers.getVoltageType(connection, fromComponentInstance, toComponentInstance);
    },

    getAvailableVoltageTypes: (connection?: DiagramConnection | null) => {
        if (!connection || !connection.from || !connection.to) {
            return [];
        }

        const { from, to } = connection;
        const fromComponentInstance = ComponentInstanceService.getById(from.componentInstanceId)!;
        const toComponentInstance = ComponentInstanceService.getById(to.componentInstanceId)!;

        return ConnectionHelpers.getAvailableVoltageTypes(connection, fromComponentInstance, toComponentInstance);
    },

    getSuggestedAndForbiddenAnchors: (
        from: DiagramConnectionAnchor,
    ): { suggested: DiagramConnectionAnchor[]; forbidden: DiagramConnectionAnchor[] } => {
        const { componentInstanceId, edge, offset } = from;

        const forbidden: DiagramConnectionAnchor[] = [];
        const suggested: DiagramConnectionAnchor[] = [];

        const { position } = diagram.componentInstances[componentInstanceId];

        const tb = edge === 'top' || edge === 'bottom';
        const X = position.x + (tb ? offset : 0);
        const Y = position.y + (tb ? 0 : offset);

        // For all 10 cells in close vicinity, check if a connection can be made
        const componentInstanceIds = [componentInstanceId];
        for (let x = X - 10; x <= X + 10; x++) {
            for (let y = Y - 10; y <= Y + 10; y++) {
                const componentInstanceId = derivedDiagramState.grid[y]?.[x];

                if (!componentInstanceId) continue;

                if (componentInstanceIds.includes(componentInstanceId)) continue;
                componentInstanceIds.push(componentInstanceId);

                const componentInstance = diagram.componentInstances[componentInstanceId];
                const anchors = ComponentInstanceService.getAnchors(componentInstance);

                for (const anchor of anchors) {
                    const isValid = ConnectionService.isValidConnection(from, anchor);

                    if (isValid) {
                        suggested.push(anchor);
                    } else {
                        forbidden.push(anchor);
                    }
                }
            }
        }

        return {
            suggested,
            forbidden,
        };
    },

    linkCable: ({ connection, cable }: { connection: DiagramConnection; cable: Cable }) => {
        InternalTrackingService.track('diagram.connection.link', {
            connection,
            cable,
        });

        const diagram = DiagramSyncService.get();

        const fieldsToSet = CableHelpers.mapFieldsToConnection(cable);

        Object.entries(fieldsToSet).forEach(([fieldPath, fieldValue]) => {
            SyncEngineHelpers.set(diagram.connections[connection.id], fieldPath, fieldValue);
        });
    },

    unlinkCable: (connectionId: string) => {
        DiagramSyncService.updateByKey(`connections.${connectionId}.cableId`, '', true);

        InternalTrackingService.track('diagram.connection.unlink', {
            connectionId,
        });
    },

    getDefaultInstallationProperties: () => {
        const standard = currentProjectState.project?.wireSizing?.standard || 'IEC';

        const installationDefaults = LocalStorageService.get('connectionInstallationDefaults') || {};

        const method = installationDefaults.method;
        const referenceMethod = installationDefaults.referenceMethod;

        if (standard === 'NEC') {
            if (method && !NECInstallationMethodOptions.find(({ value }) => value === method)) {
                installationDefaults.method = DefaultNECInstallationMethod;
            }

            // NEC does not have reference method
            if (referenceMethod) {
                installationDefaults.referenceMethod = null;
            }
        }

        if (standard === 'IEC') {
            if (method && !IECInstallationMethodOptions.find(({ value }) => value === method)) {
                installationDefaults.method = DefaultIECInstallationMethod;
            }
        }

        return installationDefaults;
    },

    setDefaultInstallationProperty: (property: string, value: any) => {
        const defaults = LocalStorageService.get('connectionInstallationDefaults') || {};
        defaults[property] = value;

        LocalStorageService.store('connectionInstallationDefaults', defaults);
    },

    suggestions: (connection: DiagramConnection) => {
        const fromComponentInstance = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(connection.to.componentInstanceId);

        const fromComponentDefinition = ComponentInstanceHelpers.definition(fromComponentInstance);
        const toComponentDefinition = ComponentInstanceHelpers.definition(toComponentInstance);

        const blocked: string[] = [
            ComponentType.validator.Enum.breaker,
            ComponentType.validator.Enum.fuse,
            ComponentType.validator.Enum.disconnect,
            ComponentType.validator.Enum.precharge,
            ComponentType.validator.Enum.contactor,
            ComponentType.validator.Enum.connector,
        ];

        const allowSuggestion = !(
            blocked.includes(toComponentInstance.componentType) || blocked.includes(fromComponentInstance.componentType)
        );

        if ((fromComponentDefinition.ports.max === 1 || toComponentDefinition.ports.max === 1) && allowSuggestion) {
            const emptyCells = ConnectionService.getEmptyCells(connection);
            const expand = ConnectionService.expand(connection);

            if (emptyCells.length > 0 || expand) {
                SuggestionService.suggest('addConnectionProtection', {
                    connection,
                    fromComponentInstance,
                    toComponentInstance,
                });
            }
        }
    },

    getCustomColumns: () => {
        const columns = new Set<string>();

        Object.values(diagram.connections).forEach((connection) => {
            Object.keys(connection.custom).forEach((key) => {
                columns.add(key);
            });
        });

        return Array.from(columns);
    },

    addCustomColumn: (name: string) => {
        const existingCustomColumns = ConnectionService.getCustomColumns();

        if (existingCustomColumns.includes(name)) {
            alert(`A column with name ${name} already exists`);
            return;
        }

        Object.values(diagram.connections).forEach((connection) => {
            connection.custom[name] = '';
        });
    },

    renameCustomColumn: (current: string, next: string) => {
        Object.values(diagram.connections).forEach((connection) => {
            connection.custom[next] = connection.custom[current] || '';
            delete connection.custom[current];
        });
    },

    removeCustomColumn: (name: string) => {
        Object.values(diagram.connections).forEach((connection) => {
            delete connection.custom[name];
        });
    },

    isThreePhase: (connection: DiagramConnection | null) => {
        if (!connection) {
            return false;
        }

        const from = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const to = ComponentInstanceService.getById(connection.to.componentInstanceId);
        const connectionVoltageType = ConnectionHelpers.getVoltageType(connection, from, to);

        if (connectionVoltageType === 'AC') {
            const numberOfLines = [
                connection.lines['AC'].L1,
                connection.lines['AC'].L2,
                connection.lines['AC'].L3,
            ].filter(Boolean).length;

            if (numberOfLines === 3) {
                return true;
            }
        }

        return false;
    },

    highlight: (id: string) => {
        highlightedConnectionState.highlightedConnection = id;
    },

    lowlight: () => {
        highlightedConnectionState.highlightedConnection = null;
    },

    moveIntoView: (id: string) => {
        const connection = ConnectionService.getById(id);

        if (connection) {
            ComponentInstanceService.moveIntoView(
                [connection.from.componentInstanceId, connection.to.componentInstanceId],
                true,
            );
        }
    },

    getEmptyCells: (connection: DiagramConnection) => {
        const points = ConnectionService.getPathPoints(connection.from, connection.to) || [];

        const cells: { position: DiagramPosition; componentInstance: DiagramComponentInstance | null }[] = [];
        const visitedCells = new Set<string>();

        for (let i = 1; i < points.length; i++) {
            const p1 = points[i - 1];
            const p2 = points[i];

            const x1 = Math.floor(p1.x / CELL_WIDTH);
            const y1 = Math.floor(p1.y / CELL_HEIGHT);
            const x2 = Math.floor(p2.x / CELL_WIDTH);
            const y2 = Math.floor(p2.y / CELL_HEIGHT);

            if (x1 === x2) {
                const minY = Math.min(y1, y2);
                const maxY = Math.max(y1, y2);
                for (let y = minY; y <= maxY; y++) {
                    const key = `${x1},${y}`;
                    if (!visitedCells.has(key)) {
                        visitedCells.add(key);

                        cells.push({
                            position: { x: x1, y },
                            componentInstance: DiagramService.getComponentInstanceAtPosition({ x: x1, y }),
                        });
                    }
                }
            } else if (y1 === y2) {
                const minX = Math.min(x1, x2);
                const maxX = Math.max(x1, x2);
                for (let x = minX; x <= maxX; x++) {
                    const key = `${x},${y1}`;
                    if (!visitedCells.has(key)) {
                        visitedCells.add(key);

                        cells.push({
                            position: { x, y: y1 },
                            componentInstance: DiagramService.getComponentInstanceAtPosition({ x, y: y1 }),
                        });
                    }
                }
            }
        }

        const emptyCells: DiagramPosition[] = [];

        cells.forEach((cell) => {
            if (!cell.componentInstance) {
                emptyCells.push(cell.position);
            }
        });

        return emptyCells;
    },

    expand: (connection: DiagramConnection) => {
        const fromComponentInstance = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(connection.to.componentInstanceId);

        const fromConnections = ComponentInstanceService.getConnections(fromComponentInstance);
        const toConnections = ComponentInstanceService.getConnections(toComponentInstance);

        const orientation = ConnectionHelpers.getOrientation(connection, fromComponentInstance, toComponentInstance);

        if (!orientation) {
            return false;
        }

        const moves = {
            n: { x: 0, y: -1 },
            ne: { x: 0, y: -1 },
            nw: { x: 0, y: -1 },
            s: { x: 0, y: 1 },
            se: { x: 0, y: 1 },
            sw: { x: 0, y: 1 },
            e: { x: -1, y: 0 },
            w: { x: 1, y: 0 },
        };

        const flipped = {
            n: moves.s,
            ne: moves.se,
            nw: moves.sw,
            s: moves.n,
            se: moves.ne,
            sw: moves.nw,
            e: moves.e,
            w: moves.w,
        };

        const safeToPlace = (componentInstance: DiagramComponentInstance, next: DiagramPosition) => {
            return DiagramService.safeToPlace(
                {
                    x: next.x,
                    y: next.y,
                    colSpan: componentInstance.colSpan,
                    rowSpan: componentInstance.rowSpan,
                },
                componentInstance.id,
            );
        };

        if (fromConnections.length > toConnections.length) {
            const next = {
                x: toComponentInstance.position.x + moves[orientation].x,
                y: toComponentInstance.position.y + moves[orientation].y,
            };

            if (!safeToPlace(toComponentInstance, next)) {
                return false;
            }

            return () => {
                toComponentInstance.position.x = next.x;
                toComponentInstance.position.y = next.y;
            };
        } else {
            const next = {
                x: fromComponentInstance.position.x + flipped[orientation].x,
                y: fromComponentInstance.position.y + flipped[orientation].y,
            };

            if (!safeToPlace(fromComponentInstance, next)) {
                return false;
            }

            return () => {
                fromComponentInstance.position.x = next.x;
                fromComponentInstance.position.y = next.y;
            };
        }
    },

    combine: (a: DiagramConnection, b: DiagramConnection) => {
        const counts = [a, b].reduce(
            (counts, connection) => {
                counts[connection.from.componentInstanceId] = (counts[connection.from.componentInstanceId] ?? 0) + 1;
                counts[connection.to.componentInstanceId] = (counts[connection.to.componentInstanceId] ?? 0) + 1;

                return counts;
            },
            {} as Record<string, number>,
        );

        const from = counts[a.from.componentInstanceId] === 1 ? a.from : a.to;
        const to = counts[b.from.componentInstanceId] === 1 ? b.from : b.to;

        const combined = DiagramConnectionSchema.parse({
            id: ConnectionHelpers.generateId(),
            from,
            to,
            length: {
                value: (a.length.value || 0) + (b.length.value || 0),
                unit: a.length.unit,
            },
            resistance: {
                nom: (a.resistance.nom || 0) + (b.resistance.nom || 0),
                unit: a.resistance.unit,
            },
            inductance: {
                nom: (a.inductance.nom || 0) + (b.inductance.nom || 0),
                unit: a.inductance.unit,
            },
            createdAt: new Date().toISOString(),
            createdBy: currentUserState.user?.id ?? '',
        });

        const ignored = ['from', 'to', 'resistance.nom', 'inductance.nom', 'length.value'];
        const combinable = [...Object.keys(crush(a)), ...Object.keys(crush(b))].filter((key) => {
            return !ignored.some((needle) => {
                return key.startsWith(needle);
            });
        });

        combinable.forEach((key) => {
            const aaa = get(a, key);
            const bbb = get(b, key);

            if (aaa !== undefined && bbb !== undefined && JSON.stringify(aaa) === JSON.stringify(bbb)) {
                SyncEngineHelpers.set(combined, key, aaa);
            }
        });

        diagram.connections[combined.id] = combined;
    },

    branch: (connection: DiagramConnection) => {
        const checked: DiagramComponentInstance['id'][] = [];
        const extended: DiagramConnection[] = [connection];

        const check = (endpoint: DiagramComponentInstance) => {
            if (checked.includes(endpoint.id)) {
                return;
            }

            checked.push(endpoint.id);

            const definition = ComponentInstanceHelpers.definition(endpoint);

            if (definition.ports.displayAsSinglePort) {
                const connections = ComponentInstanceService.getConnections(endpoint);

                connections.forEach((connection) => {
                    if (!extended.some((needle) => needle.id === connection.id)) {
                        extended.push(connection);
                    }

                    const from = ComponentInstanceService.getById(connection.from.componentInstanceId);
                    const to = ComponentInstanceService.getById(connection.to.componentInstanceId);

                    check(from);
                    check(to);
                });
            }
        };

        const from = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const to = ComponentInstanceService.getById(connection.to.componentInstanceId);

        check(from);
        check(to);

        return extended;
    },

    zone: (connection: DiagramConnection) => {
        const zones = zonesState.zones.control;

        return zones.find((zone) => zone.connections.some((x) => x.id === connection.id));
    },
};

export { ConnectionService };
